import { z } from 'zod';
import { BullMQPayloadBaseSchema, JobRedisStateBaseSchema } from './base.js';

/** Payload enqueued via JpJobCollector.queueJob(). */
export const JurisprudenceSearchPayloadSchema = BullMQPayloadBaseSchema.passthrough();
export type JurisprudenceSearchPayload = z.infer<typeof JurisprudenceSearchPayloadSchema>;

/**
 * Jurisprudence search job schemas.
 *
 * Redis key namespace: `job:{id}:jurisprudence` (migrated from `jp_job:{id}`
 * in DEBT-064 — atomic cutover, no dual-write).
 *
 * Sub-keys:
 *   - `job:{id}:jurisprudence`           — job state (status, input, progress)
 *   - `job:{id}:jurisprudence:results`   — search output (sources, jurisprudences)
 *   - `job:{id}:jurisprudence:cancelled` — cooperative cancellation flag (TTL 5min)
 *   - `job:{id}:progress:jurisprudence`  — Pub/Sub channel for progress events
 *
 * Schema stays prefix-agnostic — the prefix concern is separate from the
 * value shape. Schemas use `.passthrough()` for the broader DEBT-069
 * tightening window (deadline 2026-06-15), not for DEBT-064 specifically.
 */
export const JurisprudenceSearchJobDataSchema = JobRedisStateBaseSchema
  .extend({
    // JP jobs have a human-in-the-loop validation phase absent from other processors,
    // which extends the base status lifecycle with two domain-specific states:
    //   - 'pending_validation' — tool created the job, waiting for user confirm in the UI
    //     (MasterLoop.ts filters on this to defer addToolResult)
    //   - 'queued'             — user validated, JpJobCollector is about to enqueue BullMQ
    //     (this is the value the processor observes at its first safeParse)
    // Overriding here keeps JobRedisStateBaseSchema clean for the 6 other processors.
    // DEBT-064 originally coerced tests to 'pending' as a workaround; the real fix is here.
    status: z
      .enum([
        'pending',
        'pending_validation',
        'queued',
        'processing',
        'completed',
        'partially_completed',
        'failed',
        'cancelled',
      ])
      .optional(),
    jobId: z.string(),
    requestId: z.string().optional(),
    userId: z.string().nullable().optional(),
    // NB : l'ancien champ `mode: 'quick' | 'deep'` a été retiré (2026-07-03) — un job
    // porte désormais UNE question, sans mode. Les vieux jobs Redis qui portent encore
    // `mode` passent par le `.passthrough()`.
    input: z
      .object({
        questions: z.array(z.unknown()).optional(),
        jurisdictions: z.array(z.unknown()).optional(),
        fondements: z.array(z.unknown()).optional(),
        // Top-K rerank par question (décisions jugées en Step-2). Absent = défaut
        // maîtres (QUESTION_TOP_K=20) ; le sous-agent le fixe à 10 (2026-07-09).
        topK: z.number().int().positive().optional(),
      })
      .passthrough(),
  })
  .passthrough();
export type JurisprudenceSearchJobData = z.infer<typeof JurisprudenceSearchJobDataSchema>;
